import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';

@customElement('cadence-class-detail-page')
export class CadenceClassDetailPage extends LitElement {
  @property() classId = '';
  @state() private cls: any = null;
  @state() private students: any[] = [];
  @state() private sessions: any[] = [];
  @state() private allStudents: any[] = [];
  @state() private enrollId = '';
  @state() private loading = true;

  static styles = css`
    :host { display: block; }
    .back { color: var(--color-primary); text-decoration: none; font-size: 0.88rem; }
    h1 { margin: var(--space-3) 0 4px; font-size: 1.5rem; }
    .sub { color: var(--color-text-secondary); margin: 0 0 var(--space-4); }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--space-4); margin-bottom: var(--space-5); }
    .field .k { color: var(--color-text-secondary); font-size: 0.8rem; }
    .field .v { font-weight: 600; }
    .row { display: flex; gap: 10px; align-items: flex-end; margin-bottom: var(--space-3); flex-wrap: wrap; }
    .cards { display: grid; gap: var(--space-4); grid-template-columns: 1fr 1fr; }
    @media (max-width: 900px) { .cards { grid-template-columns: 1fr; } }
    .rm { cursor: pointer; color: var(--color-danger); font-size: 0.85rem; background: none; border: none; }
    .sess { display: flex; align-items: center; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--color-border); }
    .sess:last-child { border-bottom: none; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }
  updated(c: Map<string, unknown>) { if (c.has('classId')) this.load(); }

  private async load() {
    if (!this.classId) return;
    this.loading = true;
    try {
      const [cls, students, sessions, all] = await Promise.all([
        api.get<any>(`/classes/${this.classId}`),
        api.get<any[]>(`/classes/${this.classId}/students`),
        api.get<any[]>(`/classes/${this.classId}/sessions`),
        api.get<any>('/students?limit=100'),
      ]);
      this.cls = cls; this.students = students; this.sessions = sessions; this.allStudents = all.students;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private async enroll() {
    if (!this.enrollId) return;
    try {
      await api.post(`/classes/${this.classId}/enroll`, { student_id: this.enrollId });
      this.enrollId = '';
      showToast(t('classes.toast.enrolled'), 'success');
      await this.load();
    } catch (e) { showToast((e as Error).message, 'error'); }
  }
  private async unenroll(studentId: string) {
    await api.del(`/classes/${this.classId}/enroll/${studentId}`);
    await this.load();
  }
  private async cancelSession(sessionId: string) {
    await api.patch(`/classes/${this.classId}/sessions/${sessionId}`, { status: 'cancelled', cancellation_reason: 'Cancelled by admin' });
    await this.load();
  }

  render() {
    if (this.loading || !this.cls) return html`<cadence-loading></cadence-loading>`;
    const c = this.cls;
    const enrollable = this.allStudents.filter((s) => !this.students.some((e) => e.id === s.id));
    return html`
      <a class="back" href="/admin/classes" data-link>← ${t('classes.detail.backToClasses')}</a>
      <h1>${c.name}</h1>
      <p class="sub">${c.dance_type_name ?? ''} · ${c.level_name ?? ''} · ${c.teacher_name ?? t('classes.detail.noTeacher')}</p>
      <div class="grid">
        <div class="field"><div class="k">${t('classes.detail.day')}</div><div class="v">${c.day_of_week ? t(`classes.days.${c.day_of_week}`) : '—'}</div></div>
        <div class="field"><div class="k">${t('classes.detail.time')}</div><div class="v">${c.start_time}–${c.end_time}</div></div>
        <div class="field"><div class="k">${t('classes.detail.capacity')}</div><div class="v">${c.capacity ?? t('classes.detail.unlimited')}</div></div>
        <div class="field"><div class="k">${t('classes.detail.enrolled')}</div><div class="v">${c.enrolled_count}</div></div>
      </div>

      <div class="cards">
        <cadence-card heading=${t('classes.detail.enrolledStudents')}>
          <div class="row">
            <cadence-select placeholder=${t('classes.detail.addStudentPlaceholder')} .value=${this.enrollId}
              .options=${enrollable.map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name}` }))}
              @change=${(e: CustomEvent) => (this.enrollId = e.detail)}></cadence-select>
            <cadence-button @click=${() => this.enroll()}>${t('classes.detail.enroll')}</cadence-button>
          </div>
          ${this.students.length
            ? this.students.map((s) => html`<div class="sess"><span>${s.first_name} ${s.last_name}</span><button class="rm" @click=${() => this.unenroll(s.id)}>${t('classes.detail.remove')}</button></div>`)
            : html`<cadence-empty-state heading=${t('classes.detail.noStudents')}></cadence-empty-state>`}
        </cadence-card>

        <cadence-card heading=${t('classes.detail.upcomingSessions')}>
          ${this.sessions.length
            ? this.sessions.map((s) => html`
              <div class="sess">
                <span>${s.date}
                  <cadence-badge variant="status" color=${s.status === 'cancelled' ? 'var(--color-danger)' : 'var(--color-success)'}>${t(`classes.sessionStatus.${s.status}`)}</cadence-badge>
                </span>
                ${s.status === 'scheduled' ? html`<button class="rm" @click=${() => this.cancelSession(s.id)}>${t('common.actions.cancel')}</button>` : html`<span></span>`}
              </div>`)
            : html`<cadence-empty-state heading=${t('classes.detail.noSessions')}></cadence-empty-state>`}
        </cadence-card>
      </div>
    `;
  }
}
